import React from "react";
import { useField, useFormikContext } from "formik";
import { FileUploadButton, Stack, Text, Box } from "@origin-digital/ods-core";
import { IconError } from "@origin-digital/ods-icons";
import { apiClient } from "src/api/apiService";
import { FileUploadFormikFieldProps, FormValues, UploadedFile } from "./types";
import { FileUploadStatusWrapper } from "./FileUploadStatusWrapper";
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from "./const";
import { normalizeFileName } from "./helper";

export const FileUploadFormikField: React.FC<FileUploadFormikFieldProps> = ({
  heading,
  draftId,
  accountId,
  documentTypes,
  field,
  setDisplayingSnackbar,
}) => {
  const { setFieldValue, values } = useFormikContext<FormValues>();
  const [, { error: validationError }] = useField(field.name);
  const [uploadedFiles, setUploadedFiles] = React.useState<UploadedFile[]>([]);
  const isUploading = uploadedFiles.some((f) => f.isUploading);
  const disabledUploadButton =
    isUploading ||
    (documentTypes.length === 1 && uploadedFiles.length === 1) ||
    uploadedFiles.length >= 2;

  React.useEffect(() => {
    const fieldName = field.name as
      | "coesFiles"
      | "mcoesFiles"
      | "ewrFiles"
      | "customerConsentFiles";
    if (values[fieldName]) {
      setUploadedFiles(values[fieldName]!);
    }
  }, [
    values[
      (field.name as "coesFiles" | "mcoesFiles" | "ewrFiles",
      "customerConsentFiles")
    ],
  ]);

  React.useEffect(() => {
    const [gasOption, fieldNameForFiles] = field.name.split(".");
    if (gasOption && fieldNameForFiles) {
      // @ts-expect-error: it works but throws error in ts
      const gasFiles = values[gasOption][fieldNameForFiles];
      if (gasFiles) {
        setUploadedFiles(gasFiles);
      }
    }
  }, [
    field.name &&
      (field.name.includes("appliancesChecklistFiles") ||
        field.name.includes("sitePlanFiles")),
  ]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    console.log(file);
    if (!file || uploadedFiles.length >= 2) return;

    // file type check
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setDisplayingSnackbar({
        message:
          "Accepted types: pdf, doc/docx, jpg/jpeg, png, heif, webp, xls/xlsm/xlsx.",
        tone: "critical",
      });
      event.target.value = "";
      return;
    }

    // file size check
    if (file.size > MAX_FILE_SIZE) {
      setDisplayingSnackbar({
        message: "File too large; maximum 5MB",
        tone: "critical",
      });
      event.target.value = "";
      return;
    }

    const fileType =
      uploadedFiles.length === 0
        ? documentTypes[0]
        : documentTypes.find((dt) => dt !== uploadedFiles[0].fileType)!;

    const formData = new FormData();
    formData.append("file", file);

    const attachmentMeta = {
      file_name: normalizeFileName(file!.name),
      file_upload_type: fileType,
    };

    formData.append(
      "attachmentMeta",
      new Blob([JSON.stringify(attachmentMeta)], { type: "application/json" })
    );

    const uploadEndpoint = draftId
      ? `/draft/attachment/upload/${draftId}`
      : `/draft/account/attachment/upload/${accountId}`;

    try {
      const response = await apiClient.post(uploadEndpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const attachmentId = response.data;

      const newFile: UploadedFile = {
        name: normalizeFileName(file!.name),
        attachmentId,
        isUploading: true,
        progress: 0,
        fileType,
      };

      setUploadedFiles((prevFiles) => [...prevFiles, newFile]);

      const intervalId = setInterval(async () => {
        const progressResponse = await apiClient.get(
          `/draft/attachment/upload/progress/${attachmentId}`
        );
        const progress = progressResponse.data;

        setUploadedFiles((prevFiles) =>
          prevFiles.map((f) =>
            f.attachmentId === attachmentId ? { ...f, progress } : f
          )
        );

        if (progress === 100) {
          clearInterval(intervalId);
          setUploadedFiles((prevFiles) => {
            const updatedFiles = prevFiles.map((f) =>
              f.attachmentId === attachmentId ? { ...f, isUploading: false } : f
            );
            setFieldValue(field.name, updatedFiles);
            return updatedFiles;
          });
        }
      }, 1000);
    } catch (error) {
      setDisplayingSnackbar({
        message: "Upload failed. Please try again.",
        tone: "critical",
      });
    } finally {
      event.target.value = "";
    }
  };

  const handleRemoveFile = async (attachmentId: string) => {
    try {
      await apiClient.delete(`/draft/attachment/${attachmentId}`);
      setUploadedFiles((prevFiles) => {
        const updatedFiles = prevFiles.filter(
          (f) => f.attachmentId !== attachmentId
        );
        setFieldValue(field.name, updatedFiles);
        return updatedFiles;
      });
    } catch (e) {
      setDisplayingSnackbar({
        message: "Couldn't delete the file. Please try again.",
        tone: "critical",
      });
    }
  };

  return (
    <Stack>
      {heading && <Text>{heading}</Text>}
      {uploadedFiles.length > 0 && (
        <Stack space="medium">
          {uploadedFiles.map((file, index) => (
            <FileUploadStatusWrapper
              key={index}
              file={file}
              handleRemoveFile={handleRemoveFile}
            />
          ))}
        </Stack>
      )}
      {!disabledUploadButton && (
        <Stack space="small">
          <FileUploadButton
            variant="text"
            size="small"
            onFileUpload={(e) => {
              handleFileChange(e);
            }}
          >
            {uploadedFiles.length === 0
              ? "Upload Document"
              : "Upload Another Document"}
          </FileUploadButton>
          {/* Error helperText */}
          {validationError && (
            <Box display="flex" marginLeft="xsmall">
              <IconError color="critical" size="xsmall" />
              <Box marginLeft="xxsmall">
                <Text variant="body-small" tone="critical">
                  {validationError}
                </Text>
              </Box>
            </Box>
          )}
          {/* End Error helperTex */}
        </Stack>
      )}
    </Stack>
  );
};
